import './need-access.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LoginType } from '../../../auth/models/auth.model';
import { selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, initializeCustomer } from '../../../auth/store/auth.thunk';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { useTranslation } from '../../../i18n';
import { MembershipTier } from '../../../membership/components/membership-tier/membership-tier.component';
import type { NeedAccessReq, NeedAccessResponse } from '../../../need-access/need-access.interface';
import { AccessResponse, NeedAccessContentType } from '../../../need-access/need-access.interface';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getSearchParams } from '../../../utils/window.utils';
import { selectNeedAccess, selectNeedAccessLoading } from '../../store/content.selectors';
import { loadNeedAccess } from '../../store/content.thunk';
import { ProductCard } from '../product-card/product-card.component';

export interface NeedAccessProps {
  contentId: string;
  contentType: NeedAccessContentType;
  titleText: string;
  titleSize: string;
  description: string;
  maxWidth: string;
  // CSS Variables
  columnCount: number;
  cardBorderWidth: string;
  cardBorderRadius: number;
  cardBorderColor: string;
  cardBackgroundColor: string;
  cardTextColor: string;
  cardBadgeBackgroundColor: string;
  verticalPadding: string;
  horizontalPadding: string;
}

const NeedAccessContent: FunctionComponent<NeedAccessProps> = ({
  contentId: propContentId,
  contentType: propContentType,
  titleText: propTitleText,
  titleSize,
  description: propDescription,
  maxWidth,
  columnCount,
  cardBorderWidth,
  cardBorderRadius,
  cardBorderColor,
  cardBackgroundColor,
  cardTextColor,
  cardBadgeBackgroundColor,
  verticalPadding,
  horizontalPadding,
}) => {
  const finalContentId = useMemo(() => {
    if (propContentId && propContentId.length > 0) {
      return propContentId;
    }
    const params = getSearchParams();
    return params.get('content-id') || ''; // Return empty string instead of undefined propContentId
  }, [propContentId]);

  const finalContentType = useMemo(() => {
    if (propContentType) {
      return propContentType;
    }
    const params = getSearchParams();
    return (params.get('content-type') as NeedAccessContentType) || undefined; // Be explicit
  }, [propContentType]);

  const [titleText, setTitleText] = useState(propTitleText);
  const [description, setDescription] = useState(propDescription);

  const cssVars = {
    '--columnCount': columnCount,
    '--cardBorderWidth': cardBorderWidth,
    '--cardBorderRadius': cardBorderRadius,
    '--cardBorderColor': cardBorderColor,
    '--cardBackgroundColor': cardBackgroundColor,
    '--cardTextColor': cardTextColor,
    '--cardBadgeBackgroundColor': cardBadgeBackgroundColor,
    '--verticalPadding': verticalPadding,
    '--horizontalPadding': horizontalPadding,
  };

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectNeedAccessLoading);
  const needAccessResponse: NeedAccessResponse = useAppSelector(selectNeedAccess);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());

    if (finalContentId) {
      dispatch(
        loadNeedAccess({
          contentId: finalContentId,
          contentType: finalContentType,
        } as NeedAccessReq)
      );
    }
  }, [dispatch, finalContentId, finalContentType]);

  const hasAccess = needAccessResponse?.accessResponse === AccessResponse.ACCESS_GRANTED;
  const products = needAccessResponse?.keyProducts
    ?.map((p) => p.product)
    .filter((p) => !p.error)
    .filter((p) => p.type !== 'Membership' && p.type !== 'Fan Subscription');

  const hideOnVideoOneMembershipKey =
    finalContentType === NeedAccessContentType.VIDEO &&
    !(products || []).length &&
    needAccessResponse?.keyMemberships?.length === 1 &&
    needAccessResponse?.keyMemberships[0].tiers?.length === 1;

  // Set default titles based on access response
  useEffect(() => {
    if (!needAccessResponse) return;

    const hasMembershipKeys = needAccessResponse?.keyMemberships?.length > 0;

    if (finalContentType === NeedAccessContentType.VIDEO) {
      if (needAccessResponse?.accessResponse === AccessResponse.REQUEST_LOGIN) {
        setTitleText(t('access.logInToWatch'));
        setDescription(
          `${hasMembershipKeys ? t('access.joinOrPurchase') : t('access.purchaseAccess')}`
        );
      } else if (needAccessResponse?.accessResponse === AccessResponse.ACCESS_DENIED) {
        setTitleText(t('access.joinOrPurchase'));
        setDescription(t('access.noAccess'));
      }
    }
  }, [needAccessResponse, finalContentType]);

  const checkHasAnyKeys = (res: NeedAccessResponse | undefined): boolean => {
    if (!res) return false;
    return (
      res.keyProducts?.length > 0 || res.keyListings?.length > 0 || res.keyMemberships?.length > 0
    );
  };
  const hasAnyKeys = checkHasAnyKeys(needAccessResponse);

  return (
    <div class="single-need-access-container" style={cssVars}>
      <div className={`need-access-wrapper ${maxWidth === 'true' ? 'page-width' : ''}`}>
        {loading && (
          <section className="single-loading">
            <SpinnerComponent />
          </section>
        )}

        {!loading && !hideOnVideoOneMembershipKey && (
          <section className="single-keys-wrapper">
            {hasAnyKeys && !hasAccess ? (
              <>
                <section className="single-need-access-header">
                  <h2 className={`single-title ${titleSize}`}>{titleText}</h2>
                  {description && (
                    <div
                      className="single-description"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}
                </section>

                <section className="single-card-grid">
                  {products?.map((product, i) => (
                    <ProductCard key={`product-${i}`} product={product} />
                  ))}

                  {needAccessResponse?.keyMemberships?.map((membership, i) =>
                    membership.tiers?.map((tier, j) => (
                      <MembershipTier
                        key={`membership-${i}-tier-${j}`}
                        membership={membership}
                        accountSettings={membership.accountSettings}
                        tier={tier}
                        showProgramName={true}
                        showTierName={true}
                        showTierImage={true}
                        showQuickAdd={true}
                        showPrice={true}
                        redirectToCurrentUrl={true}
                        titleFormat="small"
                      />
                    ))
                  )}
                </section>
              </>
            ) : (
              <div>{/* No Keys or Has Access */}</div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export const NeedAccessComponent: FunctionComponent<NeedAccessProps> = (props) => {
  return (
    <Provider store={store}>
      <NeedAccessContent {...props} />
    </Provider>
  );
};
